import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly languageStorageKey = 'wfms.workorderexecution.ui.language';
  private readonly supportedLanguages = ['en', 'fr'];
  private readonly defaultLanguage = 'en';

  constructor(private translate: TranslateService) {
    this.translate.addLangs(this.supportedLanguages);
    this.translate.setDefaultLang(this.defaultLanguage);

    const storedLanguage = this.getStoredLanguage();
    const browserLanguage = this.translate.getBrowserLang();
    const initialLanguage =
      storedLanguage ??
      (browserLanguage &&
      this.supportedLanguages.includes(browserLanguage)
        ? browserLanguage
        : this.defaultLanguage);

    this.setLanguage(initialLanguage);
  }

  public getAvailableLanguages(): string[] {
    return [...this.supportedLanguages];
  }

  public getCurrentLanguage(): string {
    return this.translate.currentLang || this.defaultLanguage;
  }

  public observeLanguageChanges(): Observable<string> {
    return this.translate.onLangChange.pipe(
      map((event) => event.lang || this.getCurrentLanguage()),
      startWith(this.getCurrentLanguage())
    );
  }

  public setLanguage(languageCode: string): void {
    const languageToApply = this.supportedLanguages.includes(languageCode)
      ? languageCode
      : this.defaultLanguage;

    this.translate.use(languageToApply);
    localStorage.setItem(this.languageStorageKey, languageToApply);
  }

  private getStoredLanguage(): string | null {
    const storedLanguage = localStorage.getItem(this.languageStorageKey);

    if (!storedLanguage) {
      return null;
    }

    // Migrate legacy French code from `es` to `fr`.
    if (storedLanguage === 'es') {
      localStorage.setItem(this.languageStorageKey, 'fr');
      return 'fr';
    }

    return this.supportedLanguages.includes(storedLanguage)
      ? storedLanguage
      : null;
  }
}
